import type { FC, ReactNode } from "react";

import {
  Row,
  Column,
  Button,
  Box,
  useToast,
  Heading,
  SectionHeading,
  Text,
  EmptyState,
  ChevronRightIcon,
} from "@hightouch/htui";
import pluralize from "pluralize";

import { Navigate, useLocation, useNavigate } from "src/router";
import placeholder from "src/assets/placeholders/generic.svg";
import {
  useAccessibleOrganizationsQuery,
  usePartnerConnectLinkWorkspaceMutation,
} from "src/graphql";
import { switchWorkspace } from "src/utils/workspaces";
import { PartnerConnectLogo } from "src/pages/partner-connect";

export const Page: FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { toast } = useToast();
  const partnerConnection = (state as any)?.partnerConnection;

  const createWorkspace = () => {
    navigate("/workspaces/new", { state: { partnerConnection } });
  };

  const { data: organizationsData } = useAccessibleOrganizationsQuery(
    {},
    {
      select: (data) => data.getAccessibleOrganizations.organizations,
      suspense: true,
    },
  );

  const organizations = organizationsData ?? [];
  const hasOrganizations = organizations.length > 0;

  const { mutateAsync: linkWorkspace } =
    usePartnerConnectLinkWorkspaceMutation();

  const handleSelect = async (id, slug) => {
    if (partnerConnection) {
      try {
        await linkWorkspace({
          uuid: partnerConnection.uuid,
          workspaceId: Number(id),
        });
        navigate(`/partner-connect/${partnerConnection.uuid}`);
      } catch (err) {
        const message =
          err.message ==
          "This connection doesn't have access to the selected workspace"
            ? "There was an error creating your resource. Your user may not have the correct permissions to create the resource in the selected workspace."
            : "There was an error creating your resource. Please try again. If the error persists, please contact Hightouch support.";
        toast({
          id: "partner-connect-resource-create-error",
          title: "Error creating resource",
          message: message,
          variant: "error",
        });
      }
    } else {
      switchWorkspace(id, `/${slug}`);
    }
  };

  if (!hasOrganizations) {
    return <Navigate to="/welcome" />;
  }

  return (
    <>
      <Column gap={10} maxWidth="800px" mx="auto" width="100%">
        {partnerConnection && (
          <PartnerConnectLogo
            logo={partnerConnection.partnerLogo}
            name={partnerConnection.partnerName}
          />
        )}

        <Row align="center" justify="space-between">
          <Heading size="xl">
            Select a workspace{partnerConnection ? " to connect" : ""}
          </Heading>
          <Button onClick={createWorkspace}>Create a workspace</Button>
        </Row>

        {organizations.map((o) => (
          <Column key={o.slug} gap={2}>
            <Column>
              <Box
                color="text.tertiary"
                fontSize="sm"
                fontWeight="semibold"
                textTransform="uppercase"
              >
                Organization
              </Box>
              <SectionHeading mb={4}>{o.name ?? ""}</SectionHeading>
            </Column>
            {o.workspaces.length ? (
              <Workspaces
                workspaces={o.workspaces.filter((w) => w.is_member)}
                onSelect={handleSelect}
              />
            ) : (
              <EmptyState message="You have not been granted access to any workspaces in this organization. Please contact an organization admin." />
            )}
          </Column>
        ))}
      </Column>
    </>
  );
};

export const Workspaces: FC<{
  onSelect: (id: number, slug: string) => Promise<unknown>;
  workspaces: {
    name: string;
    id: number;
    slug: string;
    member_count: number | null;
  }[];
}> = ({ onSelect, workspaces }) => {
  return (
    <>
      {workspaces.map(({ slug, name, id, member_count }) => (
        <UserWorkspace
          key={id}
          id={id}
          name={name}
          // TODO: Get the actual number of members
          size={member_count ?? 0}
          slug={String(slug)}
          onSelect={() => {
            onSelect(id, slug || "");
          }}
        />
      ))}
    </>
  );
};

const Workspace: FC<
  Readonly<{
    slug: string;
    id: number;
    name: string;
    organization?: string;
    onClick?: () => void;
    children: ReactNode;
    bg?: string;
  }>
> = ({ name, onClick, children, bg = "white", slug }) => {
  const isButton = Boolean(onClick);

  return (
    <Row
      id={slug}
      as={isButton ? "button" : "div"}
      align="center"
      bg={bg}
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="base.border"
      borderRadius="md"
      height="56px"
      px={4}
      width="100%"
      transitionProperty="background-color"
      transitionDuration="faster"
      transitionTimingFunction="easeOutQuad"
      _focus={{ outline: "none" }}
      _focusVisible={isButton ? { boxShadow: "outline" } : undefined}
      _hover={
        isButton ? { bg: "base.background", transitionDuration: "fast" } : {}
      }
      onClick={onClick}
    >
      <Row align="center" flex={1}>
        <Text fontWeight="semibold" size="md">
          {name}
        </Text>
      </Row>

      <Row gap={4}>{children}</Row>
    </Row>
  );
};

export const UserWorkspace: FC<
  Readonly<{
    size?: number;
    slug: string;
    name: string;
    onSelect: () => void;
    id: number;
  }>
> = ({ size, onSelect, ...props }) => {
  return (
    <Workspace {...props} onClick={onSelect}>
      <Row align="center" mr={3}>
        {size} {pluralize("members", size)}
      </Row>
      <Box fontSize="18px">
        <ChevronRightIcon color="text.secondary" />
      </Box>
    </Workspace>
  );
};

export const Placeholder: FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <Column maxWidth="800px" mx="auto" p={10} width="100%">
    <EmptyState
      actions={
        <Button variant="primary" onClick={onCreate}>
          Create a workspace
        </Button>
      }
      imageUrl={placeholder}
      message="Get started by creating your first workspace. Invite people to different workspaces to keep your team’s workflows secure and organized."
      title="You have no workspaces yet"
    />
  </Column>
);
